/**
 * AdminProspects - Prospect pipeline management
 * Replaces the old mock-data AdminDashboard with real API data
 */

import React, { useState, useEffect } from 'react';
import AdminShell from './AdminShell';
import { adminFetch, navigate, hasPermission } from './api';

const STATUS_COLORS = {
  sent: '#5B9DFF', viewed: '#FFB347', accepted: '#00D4AA', expired: '#FF6B6B', draft: '#8B92A5',
};

const TIER_COLORS = {
  smb: { bg: 'rgba(0,212,170,0.12)', text: '#00D4AA' },
  midmarket: { bg: 'rgba(59,130,246,0.12)', text: '#5B9DFF' },
  enterprise: { bg: 'rgba(192,132,252,0.12)', text: '#C084FC' },
};

function formatDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function formatTime(seconds) {
  if (!seconds) return '0s';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

export default function AdminProspects() {
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    adminFetch('/api/admin/prospects')
      .then(r => r.json())
      .then(d => { setProspects(d.prospects || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const stats = {
    total: prospects.length,
    viewed: prospects.filter(p => p.status === 'viewed').length,
    accepted: prospects.filter(p => p.status === 'accepted').length,
    pending: prospects.filter(p => p.status === 'sent').length,
  };

  const filtered = filter === 'all' ? prospects : prospects.filter(p => p.status === filter);

  const s = {
    statsRow: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 },
    stat: { backgroundColor: '#1A2332', borderRadius: 12, padding: 20, border: '1px solid #2A3444' },
    statLabel: { fontSize: 12, color: '#5A6175', marginBottom: 4, textTransform: 'uppercase', fontWeight: 600 },
    statVal: { fontSize: 28, fontWeight: 700, lineHeight: 1 },
    tabs: { display: 'flex', gap: 4, marginBottom: 20 },
    tab: (active) => ({
      padding: '8px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer',
      borderRadius: 6, border: '1px solid #2A3444',
      backgroundColor: active ? '#1E293B' : 'transparent',
      color: active ? '#FFF' : '#8B92A5',
    }),
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { textAlign: 'left', padding: '12px 16px', fontSize: 11, fontWeight: 700, color: '#5A6175', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #2A3444' },
    td: { padding: '14px 16px', fontSize: 14, color: '#BBBEC7', borderBottom: '1px solid #1A2332' },
    actionBtn: {
      padding: '5px 12px', borderRadius: 6, border: '1px solid #2A3444',
      backgroundColor: 'transparent', color: '#5B9DFF', fontSize: 12, cursor: 'pointer', marginRight: 6,
    },
  };

  return (
    <AdminShell title="Prospect Pipeline">
      {/* Stats */}
      <div style={s.statsRow}>
        <div style={s.stat}>
          <div style={s.statLabel}>Total</div>
          <div style={{ ...s.statVal, color: '#FFF' }}>{stats.total}</div>
        </div>
        <div style={s.stat}>
          <div style={s.statLabel}>Viewed</div>
          <div style={{ ...s.statVal, color: '#FFB347' }}>{stats.viewed}</div>
        </div>
        <div style={s.stat}>
          <div style={s.statLabel}>Accepted</div>
          <div style={{ ...s.statVal, color: '#00D4AA' }}>{stats.accepted}</div>
        </div>
        <div style={s.stat}>
          <div style={s.statLabel}>Pending</div>
          <div style={{ ...s.statVal, color: '#5B9DFF' }}>{stats.pending}</div>
        </div>
      </div>

      {/* Filters */}
      <div style={s.tabs}>
        {['all', 'sent', 'viewed', 'accepted', 'expired'].map(tab => (
          <button key={tab} style={s.tab(filter === tab)} onClick={() => setFilter(tab)}>
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Table */}
      <div style={{ backgroundColor: '#1A2332', borderRadius: 12, border: '1px solid #2A3444', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#5A6175' }}>Loading prospects...</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#5A6175' }}>
            {prospects.length === 0 ? 'No prospects yet.' : 'No prospects match this filter.'}
          </div>
        ) : (
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Company</th>
                <th style={s.th}>Tier</th>
                <th style={s.th}>Status</th>
                <th style={s.th}>Sent</th>
                <th style={s.th}>Views</th>
                <th style={s.th}>Time</th>
                <th style={s.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => {
                const tier = TIER_COLORS[p.tier] || TIER_COLORS.smb;
                const statusColor = STATUS_COLORS[p.status] || '#5A6175';
                return (
                  <tr key={p.id}>
                    <td style={s.td}>
                      <div style={{ fontWeight: 600, color: '#FFF' }}>{p.company_name}</div>
                      <div style={{ fontSize: 12, color: '#5A6175' }}>{p.contact_name}</div>
                    </td>
                    <td style={s.td}>
                      <span style={{ padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', backgroundColor: tier.bg, color: tier.text }}>{p.tier}</span>
                    </td>
                    <td style={s.td}>
                      <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', backgroundColor: `${statusColor}20`, color: statusColor }}>{p.status}</span>
                    </td>
                    <td style={s.td}>{formatDate(p.sent_at || p.created_at)}</td>
                    <td style={s.td}>{p.view_count || 0}</td>
                    <td style={s.td}>{formatTime(p.total_time_on_page || 0)}</td>
                    <td style={s.td}>
                      {p.token && (
                        <button style={s.actionBtn} onClick={() => window.open(`/portal/${p.token}`, '_blank')}>Preview</button>
                      )}
                      <button style={s.actionBtn} onClick={() => navigate(`/admin/configure/${p.id}`)}>Configure</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </AdminShell>
  );
}
